import { isNil } from 'rambdax';
import React from 'react';
import type IReactAria from 'react-aria';
import { useOverlayTrigger, usePopover } from 'react-aria';
import type IReactStately from 'react-stately';
import { useOverlayTriggerState } from 'react-stately';

import { useClientEffect } from '@ping/hooks';
import { getScrollParents } from '@ping/utils';

import { PrimitivePopoverContext } from '../popover.context';

type OverlayTriggerStateProps = Parameters<typeof useOverlayTriggerState>[0];
type PopoverProps = Omit<Parameters<typeof usePopover>[0], 'triggerRef' | 'popoverRef'>;
export type PopoverPlacement = IReactAria.PopoverAria['placement'];

interface IPrimitivePopoverRootProps extends OverlayTriggerStateProps, PopoverProps {
  children: React.ReactNode;
  state?: IReactStately.OverlayTriggerState;
  triggerRef?: React.MutableRefObject<HTMLButtonElement>;
  boundaryRef?: React.MutableRefObject<HTMLElement>;
  onFlipped?: (placement: PopoverPlacement) => void;
}

export function PrimitivePopoverRoot(props: IPrimitivePopoverRootProps) {
  //
  // setup trigger
  const triggerRef = props.triggerRef ?? React.useRef<HTMLButtonElement>(null);
  const state = props.state ?? useOverlayTriggerState(props);
  const trigger = useOverlayTrigger({ type: 'dialog' }, state, triggerRef);
  //
  // setup panel
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const popover = usePopover({ ...props, triggerRef: props.boundaryRef ?? triggerRef, popoverRef }, state);
  //
  // Adjust panel inline size to the boundary element
  if (!isNil(props.boundaryRef?.current)) {
    const bound = props.boundaryRef.current.getBoundingClientRect();
    popover.popoverProps.style.minInlineSize = `${bound.width}px`;
    popover.popoverProps.style.maxInlineSize = `${bound.width}px`;
  }

  useClientEffect(() => props.onFlipped?.(popover.placement), [popover.placement]);

  // Close popover when a scroll parent scrolls (prevents detached dropdown panels)
  React.useEffect(() => {
    if (!state.isOpen) {
      return;
    }

    const triggerEl = (props.boundaryRef ?? triggerRef)?.current;
    if (!triggerEl) {
      return;
    }

    const scrollParents = getScrollParents(triggerEl);
    if (!scrollParents.length) {
      return;
    }

    const controller = new AbortController();
    const handleScroll = () => state.close();
    for (const parent of scrollParents) {
      parent.addEventListener('scroll', handleScroll, { passive: true, signal: controller.signal });
    }

    return () => controller.abort();
  }, [state.isOpen]);

  return (
    <PrimitivePopoverContext.Provider value={{ state, trigger, triggerRef, popover, popoverRef }}>
      {props.children}
    </PrimitivePopoverContext.Provider>
  );
}
